import { canModerateFeed } from '../../../../helpers/feed-moderation';
import { useEffect, useState } from 'react';
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  FeedApproval,
  FeedModerationFilters,
  staffFeed,
} from '../../../../api/feed.api';
import { useAppSelector } from '../../../../hooks';
import { authSelector } from '../../../../store/slices';

export default function FeedApprovals() {
  const { user } = useAppSelector(authSelector);
  const [selected, setSelected] = useState<FeedApproval | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState<Map<string, FeedApproval>>(new Map());
  const [notice, setNotice] = useState('');
  const [batchError, setBatchError] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [batchReason, setBatchReason] = useState('');
  const [progress, setProgress] = useState<{
    decision: 'APPROVE' | 'REJECT';
    done: number;
    total: number;
  } | null>(null);
  const [filters, setFilters] = useState<FeedModerationFilters>({
    status: 'PENDING',
    source: 'ALL',
    kind: 'ALL',
    author: '',
  });
  const client = useQueryClient();
  const allowed = canModerateFeed(user?.admin);
  const enabled = allowed && process.env.REACT_APP_FEED_ENABLED === 'true';
  const queue = useInfiniteQuery({
    queryKey: ['staff-feed', user?.id, 'moderation', filters],
    queryFn: ({ pageParam }) => staffFeed.moderation(filters, pageParam),
    getNextPageParam: (page) => page.nextCursor || undefined,
    enabled,
    refetchInterval: 30000,
  });
  useEffect(() => {
    setSelected(null);
    setReason('');
    setError('');
    setChecked(new Map());
    setNotice('');
    setBatchError('');
    setRejecting(false);
    setBatchReason('');
  }, [filters]);
  const post = useQuery({
    queryKey: ['staff-feed', user?.id, 'post', selected?.postId],
    queryFn: () => staffFeed.post(selected!.postId),
    enabled: enabled && !!selected,
  });
  const items = queue.data?.pages.flatMap((page) => page.items) || [];
  const actionableItems = items.filter((item) => item.status === 'PENDING');
  const checkedItems = actionableItems
    .filter((item) => checked.has(itemKey(item)))
    .map((item) => checked.get(itemKey(item))!);
  const allChecked =
    actionableItems.length > 0 &&
    actionableItems.every((item) => checked.has(itemKey(item)));
  const toggle = (item: FeedApproval, include: boolean) => {
    setChecked((current) => {
      const next = new Map(current);
      if (include) next.set(itemKey(item), item);
      else next.delete(itemKey(item));
      return next;
    });
  };
  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ['staff-feed'] }),
      client.invalidateQueries({ queryKey: ['staff-beta'] }),
    ]);
  };
  const reviewChecked = async (decision: 'APPROVE' | 'REJECT') => {
    if (busy || !checkedItems.length) return;
    const rejectionReason = batchReason.trim();
    if (decision === 'REJECT' && !rejectionReason) return;
    const batch = checkedItems;
    const outcome = decision === 'APPROVE' ? 'approved' : 'rejected';
    setBusy(true);
    setNotice('');
    setBatchError('');
    setError('');
    setProgress({ decision, done: 0, total: batch.length });
    let succeeded = 0;
    let conflicts = 0;
    let failed = 0;
    try {
      for (const item of batch) {
        try {
          if (decision === 'REJECT') {
            await staffFeed.review(item, decision, rejectionReason);
          } else {
            await staffFeed.review(item, decision);
          }
          succeeded++;
        } catch (failure: any) {
          if (failure?.response?.status === 409) conflicts++;
          else failed++;
        }
        setProgress({
          decision,
          done: succeeded + conflicts + failed,
          total: batch.length,
        });
      }
      setChecked(new Map());
      setRejecting(false);
      setBatchReason('');
      if (
        selected &&
        batch.some((item) => itemKey(item) === itemKey(selected))
      ) {
        setSelected(null);
        setReason('');
      }
      setNotice(
        `${succeeded} ${succeeded === 1 ? 'item' : 'items'} ${outcome}.`
      );
      if (conflicts || failed) {
        setBatchError(
          [
            conflicts
              ? `${conflicts} ${
                  conflicts === 1
                    ? 'item changed or was'
                    : 'items changed or were'
                } already reviewed.`
              : '',
            failed
              ? `${failed} ${
                  failed === 1 ? 'item could' : 'items could'
                } not be ${outcome}.`
              : '',
            'Check the refreshed list and select any remaining items to retry.',
          ]
            .filter(Boolean)
            .join(' ')
        );
      }
      await refresh();
    } finally {
      setProgress(null);
      setBusy(false);
    }
  };
  const review = async (decision: 'APPROVE' | 'REJECT') => {
    if (!selected || busy) return;
    if (decision === 'REJECT' && !reason.trim()) {
      setError('Add an internal reason before rejecting this content.');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    setBatchError('');
    try {
      await staffFeed.review(selected, decision, reason);
      toggle(selected, false);
      setSelected(null);
      setReason('');
      await refresh();
    } catch (failure: any) {
      setError(
        failure?.response?.status === 409
          ? 'This content changed or was already reviewed. Refresh and select it again.'
          : 'The decision could not be saved. Please retry.'
      );
      await refresh();
    } finally {
      setBusy(false);
    }
  };
  if (!allowed)
    return (
      <p className="p-8">You do not have permission to approve feed content.</p>
    );
  if (!enabled) return <p className="p-8">Food feed moderation is disabled.</p>;
  return (
    <section className="p-6 lg:p-10 max-w-7xl mx-auto">
      <h1 className="text-2xl font-semibold">Feed moderation</h1>
      <p className="mt-2 mb-8 text-neutral-500">
        Review posts and comments, including imported beta reviews. Rejection is
        an internal moderation decision and is not shown to the creator.
      </p>
      <div className="space-y-6">
        <div>
          <div className="grid gap-4 mb-6 sm:grid-cols-2 lg:grid-cols-4">
            <label className="text-sm">
              <span className="block font-medium mb-1">Status</span>
              <select
                aria-label="Moderation status"
                disabled={busy}
                className="border rounded w-full p-3 bg-white"
                value={filters.status}
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    status: event.target
                      .value as FeedModerationFilters['status'],
                  }))
                }
              >
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
                <option value="ALL">All</option>
              </select>
            </label>
            <label className="text-sm">
              <span className="block font-medium mb-1">Source</span>
              <select
                aria-label="Moderation source"
                disabled={busy}
                className="border rounded w-full p-3 bg-white"
                value={filters.source}
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    source: event.target
                      .value as FeedModerationFilters['source'],
                  }))
                }
              >
                <option value="ALL">All sources</option>
                <option value="BETA_IMPORT">Beta imports</option>
                <option value="REVIEW_IMPORT">Review imports</option>
                <option value="USER_CREATED">User-created</option>
              </select>
            </label>
            <label className="text-sm">
              <span className="block font-medium mb-1">Type</span>
              <select
                aria-label="Content type"
                disabled={busy}
                className="border rounded w-full p-3 bg-white"
                value={filters.kind}
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    kind: event.target.value as FeedModerationFilters['kind'],
                  }))
                }
              >
                <option value="ALL">Posts and comments</option>
                <option value="POST">Posts</option>
                <option value="COMMENT">Comments</option>
              </select>
            </label>
            <label className="text-sm">
              <span className="block font-medium mb-1">Author</span>
              <input
                aria-label="Author name or username"
                disabled={busy}
                className="border rounded w-full p-3"
                placeholder="Name or username"
                maxLength={100}
                value={filters.author || ''}
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    author: event.target.value,
                  }))
                }
              />
            </label>
          </div>
          <button
            className="text-sm text-orange-700 mb-5 disabled:text-neutral-400"
            disabled={
              busy ||
              (filters.status === 'PENDING' &&
                filters.source === 'ALL' &&
                filters.kind === 'ALL' &&
                !filters.author)
            }
            onClick={() =>
              setFilters({
                status: 'PENDING',
                source: 'ALL',
                kind: 'ALL',
                author: '',
              })
            }
          >
            Clear filters
          </button>
          {queue.isLoading && <p>Loading moderation content…</p>}
          {queue.isError && (
            <button onClick={() => queue.refetch()}>
              Couldn’t load moderation content. Retry
            </button>
          )}
          {!!items.length && (
            <>
              <div className="flex gap-4 items-center flex-wrap mb-3">
                <label className="flex gap-2 items-center text-sm">
                  <input
                    type="checkbox"
                    aria-label="Select all loaded items"
                    checked={allChecked}
                    disabled={busy}
                    onChange={(event) =>
                      setChecked(
                        event.target.checked
                          ? new Map(
                              actionableItems.map((item) => [
                                itemKey(item),
                                item,
                              ])
                            )
                          : new Map()
                      )
                    }
                  />
                  Select all loaded
                </label>
                <button
                  className="px-4 py-3 bg-orange-600 text-white rounded-lg disabled:opacity-50"
                  onClick={() => reviewChecked('APPROVE')}
                  disabled={busy || !checkedItems.length}
                >
                  {progress?.decision === 'APPROVE'
                    ? `Approving ${progress.done} of ${progress.total}…`
                    : `Approve selected (${checkedItems.length})`}
                </button>
                <button
                  className="px-4 py-3 border border-red-700 text-red-700 rounded-lg disabled:opacity-50"
                  disabled={busy || !checkedItems.length}
                  aria-expanded={rejecting}
                  aria-controls="bulk-rejection"
                  onClick={() => setRejecting(true)}
                >
                  {progress?.decision === 'REJECT'
                    ? `Rejecting ${progress.done} of ${progress.total}…`
                    : `Reject selected (${checkedItems.length})`}
                </button>
                <p className="text-sm text-neutral-500">
                  Only loaded items are selected. Load more to include the next
                  group.
                </p>
              </div>
              {rejecting && (
                <form
                  id="bulk-rejection"
                  aria-label="Reject selected content"
                  className="border rounded-lg bg-white p-4 mb-4"
                  onSubmit={(event) => {
                    event.preventDefault();
                    reviewChecked('REJECT');
                  }}
                >
                  <label
                    htmlFor="bulk-rejection-reason"
                    className="block text-sm font-medium"
                  >
                    Internal reason for selected items (not shown to creators)
                  </label>
                  <textarea
                    id="bulk-rejection-reason"
                    className="border w-full mt-2 p-3 min-h-[90px]"
                    maxLength={500}
                    required
                    autoFocus
                    disabled={busy}
                    value={batchReason}
                    onChange={(event) => setBatchReason(event.target.value)}
                  />
                  <p className="text-sm text-neutral-500 mt-2">
                    This reason will be saved for all {checkedItems.length}{' '}
                    selected items.
                  </p>
                  <div className="flex flex-wrap gap-3 mt-4">
                    <button
                      type="submit"
                      className="px-4 py-3 bg-red-700 text-white rounded-lg disabled:opacity-50"
                      disabled={
                        busy || !checkedItems.length || !batchReason.trim()
                      }
                    >
                      Confirm rejection ({checkedItems.length})
                    </button>
                    <button
                      type="button"
                      className="px-4 py-3 border rounded-lg disabled:opacity-50"
                      disabled={busy}
                      onClick={() => {
                        setRejecting(false);
                        setBatchReason('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
              <div className="overflow-x-auto border rounded-lg">
                <table
                  aria-label="Feed moderation content"
                  className="w-full min-w-[650px] text-sm text-left"
                >
                  <thead className="bg-neutral-50 text-xs text-neutral-600">
                    <tr>
                      {[
                        'Select',
                        'Review',
                        'Status',
                        'Source',
                        'Review reason',
                        'Negative',
                        'Profanity',
                        'Details',
                      ].map((label) => (
                        <th
                          key={label}
                          scope="col"
                          className="px-3 py-3 font-medium"
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr
                        key={itemKey(item)}
                        className={`border-t ${
                          selected && itemKey(selected) === itemKey(item)
                            ? 'bg-orange-50'
                            : 'bg-white'
                        }`}
                      >
                        <td className="p-3 align-top">
                          {item.status === 'PENDING' ? (
                            <input
                              type="checkbox"
                              aria-label={`Select ${item.kind.toLowerCase()} by ${
                                item.author.firstName
                              }: ${item.body}`}
                              checked={checked.has(itemKey(item))}
                              disabled={busy}
                              onChange={(event) =>
                                toggle(item, event.target.checked)
                              }
                            />
                          ) : (
                            <span className="text-neutral-400">—</span>
                          )}
                        </td>
                        <td className="p-3 align-top min-w-[220px] max-w-[420px] break-words">
                          <p className="text-xs text-neutral-500 mb-2">
                            <span className="font-medium text-neutral-700">
                              {item.author.firstName}
                            </span>
                            {' · '}
                            {item.kind === 'POST' ? 'Post' : 'Comment'}
                            {' · '}
                            {item.audience === 'PRIVATE' ? 'Private' : 'Public'}
                          </p>
                          <p className="whitespace-pre-wrap">
                            {item.body || 'No written review'}
                          </p>
                        </td>
                        <td className="p-3 align-top">
                          {statusLabel(item.status)}
                        </td>
                        <td className="p-3 align-top">
                          {sourceLabel(item.source)}
                        </td>
                        <td className="p-3 align-top break-words">
                          {reviewReason(item.reason)}
                        </td>
                        <td className="p-3 align-top">
                          <span
                            title={
                              item.negativeProbability === null
                                ? 'Unavailable'
                                : undefined
                            }
                          >
                            {item.negativeProbability === null
                              ? '—'
                              : probability(item.negativeProbability)}
                          </span>
                        </td>
                        <td className="p-3 align-top">
                          <span
                            title={
                              item.profanityProbability === null
                                ? 'Unavailable'
                                : undefined
                            }
                          >
                            {item.profanityProbability === null
                              ? '—'
                              : probability(item.profanityProbability)}
                          </span>
                        </td>
                        <td className="p-3 align-top">
                          <button
                            className="border rounded px-3 py-2 disabled:opacity-50"
                            disabled={busy}
                            aria-label={`View ${item.kind.toLowerCase()} by ${
                              item.author.firstName
                            }`}
                            onClick={() => {
                              setSelected(item);
                              setReason('');
                              setError('');
                            }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {!!notice && (
            <p role="status" className="text-green-800 mt-4">
              {notice}
            </p>
          )}
          {!!batchError && (
            <p role="alert" className="text-red-700 mt-4">
              {batchError}
            </p>
          )}
          {queue.data &&
            !queue.data.pages.some((page) => page.items.length) && (
              <p className="py-8 text-neutral-500">
                No moderation content matches these filters.
              </p>
            )}
          {queue.hasNextPage && (
            <button
              className="p-4 text-orange-700"
              disabled={busy || queue.isFetchingNextPage}
              onClick={() => queue.fetchNextPage()}
            >
              Load more
            </button>
          )}
        </div>
        {selected && (
          <article className="border border-neutral-200 bg-white">
            <header className="p-5 border-b">
              <h2 className="font-semibold">
                {selected.author.firstName} ·{' '}
                {selected.kind === 'POST' ? 'Post' : 'Comment'}
              </h2>
              <p className="text-xs text-neutral-500 mt-1">
                {statusLabel(selected.status)} · {sourceLabel(selected.source)}
              </p>
              <p className="text-xs text-neutral-500 mt-1">
                {selected.status === 'PENDING' ? 'Queued' : 'Reviewed'}{' '}
                {new Date(selected.activityAt).toLocaleString()}
              </p>
            </header>
            {post.isLoading && <p className="p-5">Loading post context…</p>}
            {post.isError && (
              <p className="p-5">
                Couldn’t load post context.{' '}
                <button onClick={() => post.refetch()}>Retry</button>
              </p>
            )}
            {post.data?.media.map((media) =>
              media.kind === 'VIDEO' ? (
                <video
                  key={media.id}
                  controls
                  preload="metadata"
                  poster={media.thumbnailUrl}
                  src={media.url}
                  className="w-full"
                />
              ) : (
                <img
                  key={media.id}
                  src={media.url}
                  alt="Food post under review"
                  className="w-full"
                />
              )
            )}
            <div className="p-5">
              <p className="whitespace-pre-wrap" data-testid="approval-text">
                {selected.body}
              </p>
              {selected.kind === 'COMMENT' && (
                <blockquote className="border-l-2 mt-5 pl-4">
                  <p className="text-xs text-neutral-500">Post context</p>
                  <p className="whitespace-pre-wrap mt-2">
                    {post.data?.caption}
                  </p>
                </blockquote>
              )}
              <dl className="mt-6 text-sm space-y-2">
                <div>
                  <dt className="inline font-medium">
                    Negative sentiment probability:{' '}
                  </dt>
                  <dd className="inline">
                    {probability(selected.negativeProbability)}
                  </dd>
                </div>
                <div>
                  <dt className="inline font-medium">
                    Profanity probability:{' '}
                  </dt>
                  <dd className="inline">
                    {probability(selected.profanityProbability)}
                  </dd>
                </div>
                {selected.reviewNote && (
                  <div>
                    <dt className="inline font-medium">
                      Internal moderation note:{' '}
                    </dt>
                    <dd className="inline">{selected.reviewNote}</dd>
                  </div>
                )}
              </dl>
              {selected.status === 'PENDING' && (
                <>
                  <label
                    className="block mt-6 text-sm font-medium"
                    htmlFor="approval-reason"
                  >
                    Internal rejection reason (not shown to creator)
                  </label>
                  <textarea
                    id="approval-reason"
                    className="border w-full mt-2 p-3 min-h-[90px]"
                    maxLength={500}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                  />
                  <div className="flex flex-wrap gap-3 mt-4">
                    <button
                      className="px-4 py-3 bg-orange-600 text-white rounded-lg disabled:opacity-50"
                      disabled={
                        busy ||
                        !post.data ||
                        post.isError ||
                        !!post.data.removed
                      }
                      onClick={() => review('APPROVE')}
                    >
                      Approve {selected.kind === 'POST' ? 'post' : 'comment'}
                    </button>
                    <button
                      className="px-4 py-3 border border-red-700 text-red-700 rounded-lg disabled:opacity-50"
                      disabled={busy || !reason.trim()}
                      onClick={() => review('REJECT')}
                    >
                      Reject {selected.kind === 'POST' ? 'post' : 'comment'}
                    </button>
                  </div>
                </>
              )}
              {error && (
                <p role="alert" className="mt-4 text-red-700">
                  {error}
                </p>
              )}
            </div>
          </article>
        )}
      </div>
    </section>
  );
}

function probability(value: number | null) {
  return value === null ? 'Unavailable' : `${Math.round(value * 100)}%`;
}

function itemKey(item: FeedApproval) {
  return `${item.kind}-${item.id}`;
}

function reviewReason(reason: string | null) {
  if (reason === 'REVIEW_IMPORT')
    return 'Review import · classification pending';
  if (reason === 'BETA_IMPORT') return 'Imported beta review';
  if (reason === 'CLASSIFIER_UNAVAILABLE')
    return 'Classifier unavailable · manual review required';
  if (reason === 'NEGATIVE') return 'Negative sentiment';
  if (reason === 'PROFANITY') return 'Profanity';
  return reason || 'Automated';
}

function statusLabel(status: FeedApproval['status']) {
  if (status === 'APPROVED') return 'Approved';
  if (status === 'REJECTED') return 'Rejected';
  return 'Pending';
}

function sourceLabel(source: FeedApproval['source']) {
  if (source === 'REVIEW_IMPORT') return 'Review import';
  if (source === 'BETA_IMPORT') return 'Beta import';
  return 'User-created';
}
